/**
 * RateLimitExhaustedState — replaces InputRow when state.errorCode==='rate_limit'
 * (Plan 05-08a Task 2).
 *
 * Shows the daily limit per role + "Prøv igjen i morgen!".
 * Admin additionally sees "Endre grenser i innstillinger" link.
 *
 * UI-SPEC §Component 9.
 *
 * Accessibility: role="alert" — replaces primary interaction surface,
 * warrants immediate announcement per UI-SPEC §Accessibility.
 *
 * #158 (steg 8a): tilstanden er ikke tegnet («Not designed yet» i PLAN.md),
 * så den restyles bare med tokenene: glasskort, ink/ink-2 og warn-ikon.
 * Oppsett og tekst er som før.
 *
 * data-testid: assistant-rate-limited
 */
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';

interface RateLimitExhaustedStateProps {
  role: 'admin' | 'teen' | 'child';
  /**
   * The server's curated `message_nb`, when the error came from an SSE error
   * event. Overrides the role-based per-member copy below.
   *
   * Needed because two different situations arrive as code `rate_limit`: the
   * member spending their own daily turn quota, and the *household* spending
   * its daily cost ceiling. Only the server knows which, and telling a child
   * "du har brukt opp dagens samtaler" when the household budget is what ran
   * out is simply wrong.
   */
  serverMessage?: string | undefined;
}

export function RateLimitExhaustedState({
  role,
  serverMessage,
}: RateLimitExhaustedStateProps) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="assistant-rate-limited"
      role="alert"
      className="flex justify-center mt-8 px-4"
    >
      <div className={`${GLASS_SURFACE} w-full max-w-md rounded-lg p-6 text-center shadow-md`}>
        <div className="mb-4 flex justify-center">
          <Icon.Bell size={32} className="text-warn" />
        </div>
        <h2 className="mb-2 text-heading-s font-extrabold text-ink">
          {t('assistant.rate_limit.title')}
        </h2>
        <div className="text-base text-ink-2 mt-2 space-y-1">
          {serverMessage !== undefined && serverMessage !== '' ? (
            <p data-testid="assistant-rate-limited-message">{serverMessage}</p>
          ) : (
            <>
              {role === 'child' && (
                <p>{t('assistant.rate_limit.limit.child')}</p>
              )}
              {role === 'teen' && (
                <p>{t('assistant.rate_limit.limit.teen')}</p>
              )}
            </>
          )}
        </div>
        <p className="text-base text-ink-2 mt-4">
          {t('assistant.rate_limit.try_tomorrow')}
        </p>
        {role === 'admin' && (
          <a
            href="/settings/admin"
            className="mt-4 inline-flex min-h-11 items-center text-base font-bold text-accent-text underline"
          >
            {t('assistant.rate_limit.admin_link')}
          </a>
        )}
      </div>
    </div>
  );
}
